/**
 * Crew codes: the name of the crew (configuration) a CREW interval's line-up matches.
 *
 *   Helmsman, Wing Trim, Flight Ctrl, G1, G2, Strategy  ->  "A + Nat"
 *
 * Each seat resolves to a roster sailor (stored `<SEAT>_id`, else a unique name / alias match). The
 * line-up then matches a crew from `{class}.crew`: the five main seats exactly (a null seat in the
 * crew = that seat empty), STRATEGY null in the crew = any strategy. When two crews match, the one
 * naming Strategy wins. No match -- an unresolved main-seat name included -- is `UNMATCHED`.
 * No seat filled in, an empty roster or no crews loaded gives no code at all, never `UNMATCHED`.
 *
 * MIRRORED in server_python/scripts/helpers/crew_code.py; both are tested against
 * frontend/__tests__/fixtures/crew_code_cases.json. See docs/backend/crew-codes.md.
 */

export const CREW_SEATS = [
  'HELMSMAN',
  'WING_TRIM',
  'FLIGHT_CTRL',
  'G1',
  'G2',
  'STRATEGY',
] as const;

export type CrewSeat = (typeof CREW_SEATS)[number];

/** The seat a crew may leave open (null = any strategy, or none). */
export const OPTIONAL_CREW_SEAT: CrewSeat = 'STRATEGY';

/**
 * Older tagger rows stored the form label next to the canonical seat key
 * (`Helmsman` and `HELMSMAN`, `Wing Trim` and `WING_TRIM`). Readers still accept the label.
 * Writers keep only the canonical key.
 */
export const CREW_TAGGER_SEAT_KEYS: Record<CrewSeat, string> = {
  HELMSMAN: 'Helmsman',
  WING_TRIM: 'Wing Trim',
  FLIGHT_CTRL: 'Flight Ctrl',
  G1: 'G1',
  G2: 'G2',
  STRATEGY: 'Strategy',
};

/** True when the legacy label is a different string from the canonical seat key (`G1` is both). */
function crewSeatHasDistinctAlias(seat: CrewSeat): boolean {
  return CREW_TAGGER_SEAT_KEYS[seat] !== seat;
}

/**
 * One name per seat: canonical key (`HELMSMAN`, `WING_TRIM`, ...).
 * A filled legacy label is copied onto the canonical key only when that key is missing or blank.
 * The label is then removed. A filled canonical key is kept as-is.
 * `crew_code`, `CREW_CODE`, `Crew_id`, and seat ids are left as they are.
 * Returns the same object when there is no legacy label to drop.
 */
export function canonicalizeCrewSeatTags<T extends Record<string, unknown>>(tags: T): T {
  let changed = false;
  const out: Record<string, unknown> = { ...tags };
  for (const seat of CREW_SEATS) {
    if (!crewSeatHasDistinctAlias(seat)) {
      continue;
    }
    const display = CREW_TAGGER_SEAT_KEYS[seat];
    if (!Object.prototype.hasOwnProperty.call(tags, display)) {
      continue;
    }
    const canonicalMissing = !Object.prototype.hasOwnProperty.call(tags, seat);
    if ((canonicalMissing || isBlankCrewValue(tags[seat])) && !isBlankCrewValue(tags[display])) {
      const alias = tags[display];
      out[seat] = typeof alias === 'string' ? alias.trim() : alias;
    }
    delete out[display];
    changed = true;
  }
  return (changed ? out : tags) as T;
}

/** The code of a line-up that matches no crew. Reserved: no crew may be called this. */
export const CREW_CODE_UNMATCHED = 'UNMATCHED';

/**
 * Temporary boat for every CREW tag. Source lists on the events are not trusted yet,
 * so the Tags panel shows crew only while this boat is selected.
 */
export const CREW_TAG_BOAT = 'GER';

/** True for non-crew tags, and for CREW only when the selected boat is GER. */
export function crewTagVisibleForSource(eventType: string, sourceName: string | null | undefined): boolean {
  if (eventType.trim().toUpperCase() !== 'CREW') {return true;}
  return (sourceName ?? '').trim().toUpperCase() === CREW_TAG_BOAT;
}

export type CrewRosterSailor = {
  sailor_id: number;
  first_name: string;
  last_name: string;
  aliases?: string[] | null;
  active?: boolean;
};

/** A row of `{class}.crew`: a named line-up, one roster sailor id per seat. */
export type CrewDefinition = {
  crew_id: number;
  name: string;
  helmsman_id: number | null;
  wing_trim_id: number | null;
  flight_ctrl_id: number | null;
  g1_id: number | null;
  g2_id: number | null;
  /** null = any strategy (or none). */
  strategy_id: number | null;
  notes?: string;
  active?: boolean;
};

type CrewSeatColumn =
  | 'helmsman_id'
  | 'wing_trim_id'
  | 'flight_ctrl_id'
  | 'g1_id'
  | 'g2_id'
  | 'strategy_id';

/** `{class}.crew` column per seat. */
export const CREW_SEAT_COLUMNS: Record<CrewSeat, CrewSeatColumn> = {
  HELMSMAN: 'helmsman_id',
  WING_TRIM: 'wing_trim_id',
  FLIGHT_CTRL: 'flight_ctrl_id',
  G1: 'g1_id',
  G2: 'g2_id',
  STRATEGY: 'strategy_id',
};

export type CrewCodeResult = {
  /** Crew name, `UNMATCHED`, or null (no seat filled in / no roster / no crews to match against). */
  code: string | null;
  crew_id: number | null;
  /** Roster sailor id per resolved seat. */
  ids: Partial<Record<CrewSeat, number>>;
  unresolved: Array<{ seat: CrewSeat; name: string }>;
};

/** `''`, `NA` and `nan` carry no value -- the same sentinels the Python tag helpers use. */
export function isBlankCrewValue(value: unknown): boolean {
  if (value == null) {return true;}
  const s = String(value).trim();
  return s === '' || s.toUpperCase() === 'NA' || s.toLowerCase() === 'nan';
}

/** Comparable form of a name: casefolded, accents stripped, dots/hyphens/underscores as spaces. */
export function normalizeCrewName(value: unknown): string {
  if (value == null) {return '';}
  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[._\-']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function sailorDisplayName(sailor: CrewRosterSailor): string {
  return [sailor.first_name, sailor.last_name].map((s) => (s ?? '').trim()).filter(Boolean).join(' ');
}

export type CrewRosterIndex = {
  byId: Map<number, CrewRosterSailor>;
  /** normalised name -> sailor ids that answer to it; more than one id = ambiguous. */
  byName: Map<string, Set<number>>;
  size: number;
};

export function buildCrewRosterIndex(roster: readonly CrewRosterSailor[] | null | undefined): CrewRosterIndex {
  const byId = new Map<number, CrewRosterSailor>();
  const byName = new Map<string, Set<number>>();
  const add = (key: string, id: number) => {
    if (!key) {return;}
    let ids = byName.get(key);
    if (!ids) {
      ids = new Set();
      byName.set(key, ids);
    }
    ids.add(id);
  };
  for (const sailor of roster ?? []) {
    const id = Number(sailor.sailor_id);
    if (!Number.isInteger(id)) {continue;}
    byId.set(id, sailor);
    add(normalizeCrewName(sailor.last_name), id);
    add(normalizeCrewName(sailor.first_name), id);
    add(normalizeCrewName(`${sailor.first_name ?? ''} ${sailor.last_name ?? ''}`), id);
    for (const alias of sailor.aliases ?? []) {
      add(normalizeCrewName(alias), id);
    }
  }
  return { byId, byName, size: byId.size };
}

/** The seat's name: canonical key first, then the tagger display key. Blank values are null. */
export function crewSeatName(tags: Record<string, unknown> | null | undefined, seat: CrewSeat): string | null {
  if (!tags) {return null;}
  for (const key of [seat, CREW_TAGGER_SEAT_KEYS[seat]]) {
    const value = tags[key];
    if (!isBlankCrewValue(value)) {return String(value).trim();}
  }
  return null;
}

/** Tags-panel labels for a CREW bubble. Order is the lineup the panel lists. */
const CREW_TAG_SUMMARY_FIELDS: ReadonlyArray<{
  label: string;
  read: (tags: Record<string, unknown>) => string | null;
}> = [
  { label: 'Crew', read: (tags) => firstFilledCrewTag(tags, ['crew_code', 'CREW_CODE', 'Crew']) },
  { label: 'Helm', read: (tags) => crewSeatName(tags, 'HELMSMAN') },
  { label: 'Wing', read: (tags) => crewSeatName(tags, 'WING_TRIM') },
  { label: 'Flight', read: (tags) => crewSeatName(tags, 'FLIGHT_CTRL') },
  { label: 'G1', read: (tags) => crewSeatName(tags, 'G1') },
  { label: 'G2', read: (tags) => crewSeatName(tags, 'G2') },
  { label: 'Strategy', read: (tags) => crewSeatName(tags, 'STRATEGY') },
];

function firstFilledCrewTag(tags: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = tags[key];
    if (!isBlankCrewValue(value)) {return String(value).trim();}
  }
  return null;
}

function asCrewTagRecord(tags: unknown): Record<string, unknown> | null {
  if (tags == null) {return null;}
  if (typeof tags === 'string') {
    const raw = tags.trim();
    if (!raw.startsWith('{')) {return null;}
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof tags === 'object' && !Array.isArray(tags)) {
    return tags as Record<string, unknown>;
  }
  return null;
}

/**
 * Crew tag body for the Tags panel: crew code plus helm, wing, flight, G1, G2, and strategy.
 * Blank seats read "NA". Ids, origin, interval markers, and other tag keys are omitted.
 * Empty string when `tags` is not a crew tag object.
 */
export function formatCrewTagSummary(tags: unknown): string {
  const record = asCrewTagRecord(tags);
  if (!record) {return '';}
  return CREW_TAG_SUMMARY_FIELDS
    .map(({ label, read }) => `${label}: ${read(record) ?? 'NA'}`)
    .join('\n');
}

/** Editable crew-tag fields, in the same order as the Tags bubble. */
export const CREW_TAG_EDIT_FIELDS = [
  { key: 'crew', label: 'Crew' },
  { key: 'helm', label: 'Helm' },
  { key: 'wing', label: 'Wing' },
  { key: 'flight', label: 'Flight' },
  { key: 'g1', label: 'G1' },
  { key: 'g2', label: 'G2' },
  { key: 'strategy', label: 'Strategy' },
] as const;

export type CrewTagEditField = (typeof CREW_TAG_EDIT_FIELDS)[number]['key'];
export type CrewTagEditValues = Record<CrewTagEditField, string>;

const CREW_TAG_EDIT_SEATS: ReadonlyArray<{ key: Exclude<CrewTagEditField, 'crew'>; seat: CrewSeat }> = [
  { key: 'helm', seat: 'HELMSMAN' },
  { key: 'wing', seat: 'WING_TRIM' },
  { key: 'flight', seat: 'FLIGHT_CTRL' },
  { key: 'g1', seat: 'G1' },
  { key: 'g2', seat: 'G2' },
  { key: 'strategy', seat: 'STRATEGY' },
];

function crewEditDisplay(value: string | null): string {
  return value && !isBlankCrewValue(value) ? value.trim() : 'NA';
}

function crewEditStored(value: string): string {
  const trimmed = value.trim();
  return trimmed === '' || isBlankCrewValue(trimmed) ? 'NA' : trimmed;
}

/** Seat tags for matching, from the tag-modal crew fields. Blank seats are stored as NA. */
export function crewTagsFromEditValues(form: CrewTagEditValues): Record<string, string> {
  const tags: Record<string, string> = {};
  for (const { key, seat } of CREW_TAG_EDIT_SEATS) {
    tags[seat] = crewEditStored(form[key]);
  }
  return tags;
}

function sameCrewEditValue(stored: string | null, next: string): boolean {
  return normalizeCrewName(crewEditDisplay(stored)) === normalizeCrewName(crewEditStored(next));
}

export function emptyCrewTagEditValues(): CrewTagEditValues {
  return { crew: 'NA', helm: 'NA', wing: 'NA', flight: 'NA', g1: 'NA', g2: 'NA', strategy: 'NA' };
}

/** Form values for a CREW tag. Missing seats read as NA. */
export function crewTagEditValuesFromTags(tags: unknown): CrewTagEditValues {
  const record = asCrewTagRecord(tags);
  if (!record) {return emptyCrewTagEditValues();}
  const values = emptyCrewTagEditValues();
  values.crew = crewEditDisplay(firstFilledCrewTag(record, ['crew_code', 'CREW_CODE', 'Crew']));
  for (const { key, seat } of CREW_TAG_EDIT_SEATS) {
    values[key] = crewEditDisplay(crewSeatName(record, seat));
  }
  return values;
}

/**
 * Patch for the crew fields only. Seat ids and Crew_id are removed when that name or code changes,
 * so a later match does not keep the previous sailor. Other tag keys are left untouched.
 */
export function crewTagEditPatch(existing: unknown, form: CrewTagEditValues): Record<string, unknown> {
  const record = asCrewTagRecord(existing) ?? {};
  const patch: Record<string, unknown> = {};
  const nextCrew = crewEditStored(form.crew);
  patch.crew_code = nextCrew;
  if (record.CREW_CODE != null) {patch.CREW_CODE = nextCrew;}
  if (!sameCrewEditValue(firstFilledCrewTag(record, ['crew_code', 'CREW_CODE', 'Crew']), nextCrew)) {
    patch.Crew_id = null;
  }
  for (const { key, seat } of CREW_TAG_EDIT_SEATS) {
    const next = crewEditStored(form[key]);
    patch[seat] = next;
    if (crewSeatHasDistinctAlias(seat)) {
      patch[CREW_TAGGER_SEAT_KEYS[seat]] = null;
    }
    if (!sameCrewEditValue(crewSeatName(record, seat), next)) {
      patch[`${seat}_id`] = null;
    }
  }
  return patch;
}

/** The sailor a seat resolves to: a stored id that is on the roster wins, else a unique name match. */
export function resolveCrewSeat(
  index: CrewRosterIndex,
  name: string | null,
  storedId?: unknown
): CrewRosterSailor | null {
  const id = storedId == null || storedId === '' ? NaN : Number(storedId);
  if (Number.isInteger(id) && index.byId.has(id)) {
    return index.byId.get(id) ?? null;
  }
  if (name == null) {return null;}
  const ids = index.byName.get(normalizeCrewName(name));
  if (!ids || ids.size !== 1) {return null;}
  const [only] = ids;
  return index.byId.get(only) ?? null;
}

function seatId(value: unknown): number | null {
  if (value == null || value === '') {return null;}
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

/**
 * The crew a resolved line-up matches, or null. `ids` holds only seats that resolved; a seat in
 * `unresolvedSeats` matches nothing except an open strategy.
 */
export function matchCrew(
  ids: Partial<Record<CrewSeat, number>>,
  crews: readonly CrewDefinition[] | null | undefined,
  unresolvedSeats: ReadonlySet<CrewSeat> = new Set()
): CrewDefinition | null {
  let best: CrewDefinition | null = null;
  for (const crew of crews ?? []) {
    const mainSeatsMatch = CREW_SEATS.every((seat) => {
      if (seat === OPTIONAL_CREW_SEAT) {return true;}
      if (unresolvedSeats.has(seat)) {return false;}
      return seatId(crew[CREW_SEAT_COLUMNS[seat]]) === (ids[seat] ?? null);
    });
    if (!mainSeatsMatch) {continue;}
    const strategy = seatId(crew.strategy_id);
    if (strategy == null) {
      if (!best) {best = crew;}
    } else if (!unresolvedSeats.has(OPTIONAL_CREW_SEAT) && strategy === (ids.STRATEGY ?? null)) {
      return crew; // names the strategy: beats an open-strategy crew
    }
  }
  return best;
}

type RosterArg = readonly CrewRosterSailor[] | CrewRosterIndex | null | undefined;

/** First letter of a seat name, after the same folding used to match the roster. */
export function crewConfigInitial(name: string | null | undefined): string | null {
  if (isBlankCrewValue(name)) {return null;}
  const letter = normalizeCrewName(name).match(/[a-z]/);
  return letter ? letter[0].toUpperCase() : null;
}

/**
 * 1- and 2-letter combinations of helm, wing, and flight initials, helm first.
 * The same letter from two seats is kept (`GG`). Blank seats are skipped.
 */
function crewInitialCombinations(
  helm: string | null,
  wing: string | null,
  flight: string | null
): string[] {
  const present = [helm, wing, flight].filter((letter): letter is string => letter != null && letter !== '');
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (value: string) => {
    if (value.length < 1 || value.length >= 3 || seen.has(value)) {return;}
    seen.add(value);
    out.push(value);
  };
  for (const letter of present) {push(letter);}
  for (let i = 0; i < present.length; i += 1) {
    for (let j = 0; j < present.length; j += 1) {
      if (i === j) {continue;}
      push(present[i] + present[j]);
    }
  }
  return out;
}

/**
 * A crew name that is not already taken.
 * Tries every 1- and 2-letter combination of the helm, wing, and flight initials.
 * When those are all taken, suffixes 1 through 9 on each combination, preferred name first.
 * Null when there is no initial, or 1–9 are taken on every combination.
 * Not part of the Python matcher: only the tagger and tag modal mint names.
 */
export function suggestCrewConfigName(
  helm: string | null | undefined,
  wing: string | null | undefined,
  flight: string | null | undefined,
  existingNames: readonly string[]
): string | null {
  const bases = crewInitialCombinations(
    crewConfigInitial(helm),
    crewConfigInitial(wing),
    crewConfigInitial(flight)
  );
  if (bases.length === 0) {return null;}
  const taken = new Set<string>();
  for (const name of existingNames) {
    const key = String(name ?? '').trim().toUpperCase();
    if (key) {taken.add(key);}
  }
  taken.add(CREW_CODE_UNMATCHED);
  for (const base of bases) {
    if (!taken.has(base)) {return base;}
  }
  for (const base of bases) {
    for (let n = 1; n <= 9; n += 1) {
      const candidate = `${base}${n}`;
      if (!taken.has(candidate)) {return candidate;}
    }
  }
  return null;
}

export type PlannedCrewConfig = {
  name: string;
  seats: Record<CrewSeatColumn, number | null>;
};

/**
 * A crew to insert when every filled seat is on the roster and no crew has this line-up.
 * `crews` null means the catalog failed to load: return null rather than risk a duplicate.
 * An empty list means there are no crews yet.
 */
export function planNewCrewConfig(
  tags: Record<string, unknown> | null | undefined,
  roster: RosterArg,
  crews: readonly CrewDefinition[] | null | undefined,
  reservedNames: readonly string[] = []
): PlannedCrewConfig | null {
  const index = roster && 'byId' in roster ? roster : buildCrewRosterIndex(roster as CrewRosterSailor[] | null);
  if (index.size === 0 || crews == null) {return null;}
  const ids: Partial<Record<CrewSeat, number>> = {};
  for (const seat of CREW_SEATS) {
    const name = crewSeatName(tags, seat);
    if (name == null) {continue;}
    const sailor = resolveCrewSeat(index, name, tags?.[`${seat}_id`]);
    if (!sailor) {return null;}
    ids[seat] = Number(sailor.sailor_id);
  }
  const mainFilled = CREW_SEATS.some((seat) => seat !== OPTIONAL_CREW_SEAT && ids[seat] != null);
  if (!mainFilled) {return null;}
  if (matchCrew(ids, crews, new Set())) {return null;}
  const name = suggestCrewConfigName(
    crewSeatName(tags, 'HELMSMAN'),
    crewSeatName(tags, 'WING_TRIM'),
    crewSeatName(tags, 'FLIGHT_CTRL'),
    [...crews.map((crew) => crew.name), ...reservedNames]
  );
  if (!name) {return null;}
  const seats = {} as Record<CrewSeatColumn, number | null>;
  for (const seat of CREW_SEATS) {
    seats[CREW_SEAT_COLUMNS[seat]] = ids[seat] ?? null;
  }
  return { name, seats };
}

/**
 * Why a save cannot insert a crew, or null when there is nothing to insert
 * (no main seat, or a crew already matches) or `planNewCrewConfig` would return a plan.
 */
export function crewLineupCreateBlocker(
  tags: Record<string, unknown> | null | undefined,
  roster: RosterArg,
  crews: readonly CrewDefinition[] | null | undefined
): string | null {
  const index = roster && 'byId' in roster ? roster : buildCrewRosterIndex(roster as CrewRosterSailor[] | null);
  const filled: Array<{ seat: CrewSeat; name: string }> = [];
  for (const seat of CREW_SEATS) {
    const name = crewSeatName(tags, seat);
    if (name != null) {filled.push({ seat, name });}
  }
  const mainFilled = filled.some((seat) => seat.seat !== OPTIONAL_CREW_SEAT);
  if (!mainFilled) {return null;}
  if (index.size === 0) {return 'The crew roster is not loaded, so no crew option was added.';}
  if (crews == null) {return 'Crew options could not be loaded, so none was added.';}
  const ids: Partial<Record<CrewSeat, number>> = {};
  const unresolved: string[] = [];
  for (const seat of filled) {
    const sailor = resolveCrewSeat(index, seat.name, tags?.[`${seat.seat}_id`]);
    if (!sailor) {
      unresolved.push(seat.name);
      continue;
    }
    ids[seat.seat] = Number(sailor.sailor_id);
  }
  if (unresolved.length > 0) {
    const unique = [...new Set(unresolved)];
    const quoted = unique.map((name) => `"${name}"`).join(', ');
    const verb = unique.length === 1 ? 'does not match' : 'do not match';
    return `No crew option was added because ${quoted} ${verb} one sailor on the crew roster.`;
  }
  if (matchCrew(ids, crews, new Set())) {return null;}
  const suggested = suggestCrewConfigName(
    crewSeatName(tags, 'HELMSMAN'),
    crewSeatName(tags, 'WING_TRIM'),
    crewSeatName(tags, 'FLIGHT_CTRL'),
    crews.map((crew) => crew.name)
  );
  if (!suggested) {return 'Every crew code for this line-up is already used, so no crew option was added.';}
  return null;
}

export function buildCrewCode(
  tags: Record<string, unknown> | null | undefined,
  roster: RosterArg,
  crews: readonly CrewDefinition[] | null | undefined
): CrewCodeResult {
  const index = roster && 'byId' in roster ? roster : buildCrewRosterIndex(roster as CrewRosterSailor[] | null);
  const empty: CrewCodeResult = { code: null, crew_id: null, ids: {}, unresolved: [] };
  const names = CREW_SEATS.map((seat) => crewSeatName(tags, seat));
  if (index.size === 0 || !crews?.length || names.every((n) => n == null)) {return empty;}

  const ids: CrewCodeResult['ids'] = {};
  const unresolved: CrewCodeResult['unresolved'] = [];
  CREW_SEATS.forEach((seat, i) => {
    const name = names[i];
    if (name == null) {return;}
    const sailor = resolveCrewSeat(index, name, tags?.[`${seat}_id`]);
    if (!sailor) {
      unresolved.push({ seat, name });
      return;
    }
    ids[seat] = Number(sailor.sailor_id);
  });

  const crew = matchCrew(ids, crews, new Set(unresolved.map((u) => u.seat)));
  return {
    code: crew ? String(crew.name).trim() : CREW_CODE_UNMATCHED,
    crew_id: crew ? Number(crew.crew_id) : null,
    ids,
    unresolved,
  };
}

/**
 * `tags` with `crew_code`, `Crew_id` and `<SEAT>_id` brought up to date. Ids of seats that no longer
 * resolve are removed. With no code (no roster, no crews, no seats) the tags come back unchanged,
 * including any legacy seat labels — a failed catalog must not rewrite the row.
 * When a code is written, legacy seat labels are dropped.
 */
export function applyCrewCode<T extends Record<string, unknown>>(
  tags: T,
  roster: RosterArg,
  crews: readonly CrewDefinition[] | null | undefined
): T {
  const result = buildCrewCode(tags, roster, crews);
  if (result.code == null) {return tags;}
  const out: Record<string, unknown> = { ...canonicalizeCrewSeatTags(tags) };
  for (const seat of CREW_SEATS) {
    const id = result.ids[seat];
    if (id == null) {
      delete out[`${seat}_id`];
    } else {
      out[`${seat}_id`] = id;
    }
  }
  out.crew_code = result.code;
  if (result.crew_id == null) {
    delete out.Crew_id;
  } else {
    out.Crew_id = result.crew_id;
  }
  return out as T;
}

/** Seat labels for display (legend hover titles, admin table headers). */
export const CREW_SEAT_LABELS: Record<CrewSeat, string> = {
  HELMSMAN: 'Helmsman',
  WING_TRIM: 'Wing Trim',
  FLIGHT_CTRL: 'Flight Ctrl',
  G1: 'G1',
  G2: 'G2',
  STRATEGY: 'Strategy',
};

/**
 * The line-up a crew code stands for, seat by seat.
 * An empty seat reads "—", an unnamed strategy "None", a sailor id not on the roster "? (#id)".
 * Null when the value is not the name of a known crew (case-insensitive).
 */
export function describeCrew(
  code: unknown,
  crews: readonly CrewDefinition[] | null | undefined,
  roster: readonly CrewRosterSailor[] | null | undefined
): Array<{ seat: CrewSeat; label: string; name: string }> | null {
  const key = typeof code === 'string' ? code.trim().toLowerCase() : '';
  if (!key) {return null;}
  const crew = (crews ?? []).find((c) => String(c.name ?? '').trim().toLowerCase() === key);
  if (!crew) {return null;}
  const byId = new Map((roster ?? []).map((s) => [Number(s.sailor_id), s] as const));
  return CREW_SEATS.map((seat) => {
    const id = seatId(crew[CREW_SEAT_COLUMNS[seat]]);
    let name = seat === OPTIONAL_CREW_SEAT ? 'None' : '—';
    if (id != null) {
      const sailor = byId.get(id);
      name = sailor ? sailorDisplayName(sailor) || `#${id}` : `? (#${id})`;
    }
    return { seat, label: CREW_SEAT_LABELS[seat], name };
  });
}
